/**
 * The rich text the editor writes, made safe to draw (piece 5; decision 0360).
 *
 * 🔨 The allowlist is the rule and everything else is text. This does not hunt
 * for dangerous markup and remove it, which is how a sanitiser is got past:
 * a tag is kept only when it matches an allowed name with allowed, quoted
 * attributes exactly, and every other `<` in the document is escaped into a
 * literal one. Malformed, obfuscated and unknown markup therefore survives as
 * visible text rather than as an element, which is the failure this wants.
 */

/**
 * The tags the editor's own toolbar can produce, each with the attributes it may carry.
 * 🔒 A block may carry `style`, and the style keeps its alignment alone (T272; decision 0770).
 */
export const RICH_TEXT_TAGS: Readonly<Record<string, readonly string[]>> = {
  p: ['style'], br: [], div: ['style'],
  b: [], strong: [], i: [], em: [], u: [], s: [], strike: [],
  ul: [], ol: ['start'], li: ['style'],
  blockquote: ['style'], h1: ['style'], h2: ['style'], h3: ['style'], h4: ['style'],
  a: ['href'],
};

/**
 * 🔒 The four alignments a block keeps (T272; decision 0770): what the
 * toolbar's Align left, centre, right and justify write. 🔨 A style carries
 * nothing else through: no colour, no size, no font, no position, and no
 * declaration a pasted document brought with it.
 */
export const RICH_TEXT_ALIGNMENTS = ['left', 'center', 'right', 'justify'] as const;
export type RichTextAlignment = (typeof RICH_TEXT_ALIGNMENTS)[number];

const TEXT_ALIGN = /(?:^|;)\s*text-align\s*:\s*([a-z]+)\s*(?:;|$)/i;

/** The alignment a style names, or null where it names none of the four; the kept attribute is written one way only, so storing it twice changes nothing. */
export function richTextAlignmentOf(style: string): RichTextAlignment | null {
  const named = TEXT_ALIGN.exec(style)?.[1]?.toLowerCase();
  return (RICH_TEXT_ALIGNMENTS as readonly string[]).includes(named ?? '') ? (named as RichTextAlignment) : null;
}

/**
 * 🔒 A numbered list's start (T2802): a whole number from 2 to 999 is kept, written plainly, and 1, which a list
 * opens at already, and anything else is dropped. The editor's Continue Previous List and Start At write it.
 */
export function richTextStartOf(value: string): string | null {
  const trimmed = value.trim();
  if (!/^\d{1,3}$/.test(trimmed)) return null;
  const start = Number(trimmed);
  return start >= 2 && start <= 999 ? String(start) : null;
}

/** 🔨 The one safe-link test (T672): a link may point at the web, a mailbox or a telephone; `javascript:` and `data:` are none of them. */
export const SAFE_HREF = /^(?:https?:\/\/|mailto:|tel:)/i;

/** An element whose content is code rather than prose: the element and what it holds both go. */
const CODE_ELEMENTS = /<(script|style|iframe|object|embed|noscript|template)\b[\s\S]*?<\/\1\s*>/gi;

/**
 * A tag this module is willing to consider: a name, then only double-quoted
 * attributes carrying no angle bracket. Anything else — a single-quoted value,
 * an unquoted one, a stray bracket — fails to match and is escaped as text.
 */
const TAG = /<(\/?)([a-zA-Z][a-zA-Z0-9]*)((?:\s+[a-zA-Z][a-zA-Z0-9-]*\s*=\s*"[^"<>]*")*)\s*(\/?)>/g;

const ATTRIBUTE = /([a-zA-Z][a-zA-Z0-9-]*)\s*=\s*"([^"<>]*)"/g;

/** Every markup character escaped, `&` always: for text that is going into a document as text (T951). */
export function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c);
}

/** The XML form of the same escape, for a spreadsheet part or a vector overlay (T951). */
export function escapeXml(value: string): string {
  return value.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&apos;' })[c] ?? c);
}

/** `&` escapes only where it does not already open an entity, so `&nbsp;` survives as itself (T672: the one copy). */
export function escapeText(text: string): string {
  return text.replace(/&(?!#?[a-zA-Z0-9]+;)/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** The same escape for a double-quoted attribute value: the quote goes too. */
export function escapeAttribute(value: string): string {
  return escapeText(value).replace(/"/g, '&quot;');
}

/**
 * 🔒 One consumer's allowed table (decision 0800, layered over 0770): the tags
 * it keeps with the attributes each may carry, how a kept `style` is read, and
 * how any other attribute's value is judged. The shared table below is every
 * consumer's unless it names its own; the article's is the one that does.
 */
export interface RichTextAllowance {
  tags: Readonly<Record<string, readonly string[]>>;
  /**
   * The declarations a kept `style` carries, written one way, or null where none
   * survives. It is handed the element's name too, so a consumer can keep a
   * declaration on one element alone: the article's picture width (decision 1242).
   */
  style: (value: string, element: string) => string | null;
  /** An attribute's kept value, or null to drop it; absent keeps every allowed attribute as written. */
  attribute?: (name: string, value: string) => string | null;
}

/** The shared table: the editor's own tags, and a block's alignment as the only declaration a style keeps (T272; decision 0770). */
export const RICH_TEXT_ALLOWANCE: RichTextAllowance = {
  tags: RICH_TEXT_TAGS,
  style: (value) => {
    const alignment = richTextAlignmentOf(value);
    return alignment ? `text-align: ${alignment};` : null;
  },
  attribute: (name, value) => (name === 'start' ? richTextStartOf(value) : value.trim()),
};

function attributesOf(element: string, raw: string, allowed: readonly string[], allowance: RichTextAllowance): string {
  if (allowed.length === 0) return '';
  let out = '';
  for (const match of raw.matchAll(ATTRIBUTE)) {
    const name = match[1]!.toLowerCase();
    const value = match[2]!;
    if (!allowed.includes(name)) continue;
    if (name === 'href' && !SAFE_HREF.test(value.trim())) continue;
    if (name === 'style') {
      const declarations = allowance.style(value, element);
      if (declarations) out += ` style="${escapeAttribute(declarations)}"`;
      continue;
    }
    const kept = allowance.attribute ? allowance.attribute(name, value) : value.trim();
    if (kept === null) continue;
    out += ` ${name}="${escapeAttribute(kept.trim())}"`;
  }
  // 🔨 A link the reader did not write opens away from the application, and an
  // opener a foreign page can reach back through is a hole of its own.
  return out.includes('href=') ? `${out} rel="noopener noreferrer" target="_blank"` : out;
}

/**
 * The markup an allowlist accepts, with everything else escaped into text.
 * Safe to hand to `dangerouslySetInnerHTML`, and safe to store.
 */
export function sanitizeRichText(html: string, allowance: RichTextAllowance = RICH_TEXT_ALLOWANCE): string {
  if (!html) return '';
  // The sentinel is a private-use code point, and it leaves the input first so it cannot be forged.
  const source = html.replace(/\uE000/g, '').replace(CODE_ELEMENTS, '').replace(/<!--[\s\S]*?-->/g, '');
  const kept: string[] = [];
  const held = source.replace(TAG, (whole, closing: string, rawName: string, rawAttributes: string) => {
    const name = rawName.toLowerCase();
    const allowed = allowance.tags[name];
    // An unknown tag is dropped rather than escaped: its text stays, its element does not.
    if (!allowed) return '';
    kept.push(closing ? `</${name}>` : `<${name}${attributesOf(name, rawAttributes, allowed, allowance)}>`);
    return `\uE000${kept.length - 1}\uE000`;
  });
  return escapeText(held).replace(/\uE000(\d+)\uE000/g, (_whole, index: string) => kept[Number(index)] ?? '');
}

/** True when the markup is already what the allowlist would keep: the write path's own check. */
export function richTextIsSafe(html: string): boolean {
  return sanitizeRichText(html) === html;
}

/** The entities the allowlist's own escaping writes, and the space a surface types, read back as characters. */
const ENTITIES: Readonly<Record<string, string>> = { amp: '&', lt: '<', gt: '>', quot: '"', '#39': "'", nbsp: ' ' };

/**
 * Plain text as rich text (decision 0704): escaped, a blank line opening a new
 * paragraph and a single line break kept as one. What the terms migration does
 * to the text it found, and what an import does to a clause it splits out.
 */
export function richTextFromPlain(text: string): string {
  const trimmed = text.replace(/\r\n/g, '\n').trim();
  if (!trimmed) return '';
  const escaped = escapeHtml(trimmed);
  return `<p>${escaped.replace(/\n[ \t]*\n\s*/g, '</p><p>').replace(/\n/g, '<br>')}</p>`;
}

/**
 * The words of rich text with its markup gone, each block ending a line: what
 * a sheet measures and what an emptiness check reads (decision 0704).
 */
export function richTextPlain(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|h[1-6]|blockquote)\s*>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&(#39|[a-z]+);/gi, (whole, name: string) => ENTITIES[name.toLowerCase()] ?? whole)
    .replace(/\n{2,}/g, '\n')
    .trim();
}

/** True where rich text carries no words at all, such as an editor emptied to one bare paragraph. */
export function richTextIsEmpty(html: string | null | undefined): boolean {
  return !html || richTextPlain(html).trim() === '';
}

/** The block elements a piece of rich text is split between, so a long clause can end one sheet and go on the next. */
const BLOCK_TAGS = new Set(['p', 'div', 'ul', 'ol', 'blockquote', 'h1', 'h2', 'h3', 'h4']);
const TAG_TOKEN = /<(\/?)([a-zA-Z][a-zA-Z0-9]*)[^>]*>/g;

/**
 * The top-level blocks of rich text, in order (decision 0705): each paragraph,
 * list, heading or quotation whole, and a run of text between them as a block
 * of its own. 🔨 A block is never cut: a list is one block, so its numbering
 * never restarts on the next sheet. Markup left open closes the last block.
 */
export function richTextBlocks(html: string): string[] {
  const blocks: string[] = [];
  let depth = 0;
  let start = 0;
  let loose = '';
  const flushLoose = () => {
    if (loose.trim()) blocks.push(loose.trim());
    loose = '';
  };
  for (const match of html.matchAll(TAG_TOKEN)) {
    const name = match[2]!.toLowerCase();
    if (!BLOCK_TAGS.has(name)) continue;
    const at = match.index!;
    if (match[1] === '') {
      if (depth === 0) {
        loose += html.slice(start, at);
        flushLoose();
        start = at;
      }
      depth += 1;
    } else if (depth > 0) {
      depth -= 1;
      if (depth === 0) {
        const end = at + match[0].length;
        blocks.push(html.slice(start, end));
        start = end;
      }
    }
  }
  const rest = html.slice(start);
  if (depth > 0) blocks.push(rest);
  else {
    loose += rest;
    flushLoose();
  }
  return blocks.filter((block) => block.trim() !== '');
}
